import { lazy, useEffect, useState, type ComponentType } from 'react';
import { Loading } from '../components/ui.tsx';

/** A screen module's loader that remembers the loaded module (see `screen`). */
export interface Loader<M> {
  (): Promise<M>;
  loaded: () => M | undefined;
}

export function loader<M>(load: () => Promise<M>): Loader<M> {
  let mod: M | undefined;
  return Object.assign(() => load().then((m) => (mod = m)), { loaded: () => mod });
}

/**
 * Screen `key` of a module loaded on demand (code splitting by module, App.tsx).
 * - Shell first: on a screen's first visit its code is asked for only after the header and the menu have been
 *   painted (one frame), so on a slow phone the frame of the app appears before the screen's download competes with
 *   it (Lighthouse, phone profile: docs/reviews/PERFORMANCE_REPORT.md).
 * - Back to a screen whose module is in memory: it renders directly. Going through React.lazy would suspend once
 *   more, and React then holds the reveal for up to 300 ms.
 * Both are decided once per mount, so a later re-render never swaps the component (no remount, no lost state).
 */
export function screen<M extends Record<string, unknown>, K extends keyof M>(load: Loader<M>, key: K) {
  const Lazy = lazy(async () => ({ default: (await load())[key] as ComponentType }));
  return function Screen() {
    const [Direct] = useState(() => load.loaded()?.[key] as ComponentType | undefined);
    const [go, setGo] = useState(Boolean(Direct));
    useEffect(() => {
      if (go) return;
      const start = () => setGo(true);
      // After the next paint; the timer covers a tab opened in the background (no animation frames there).
      let next = 0;
      const frame = requestAnimationFrame(() => (next = window.setTimeout(start)));
      const fallback = window.setTimeout(start, 100);
      return () => {
        cancelAnimationFrame(frame);
        clearTimeout(next);
        clearTimeout(fallback);
      };
    }, [go]);
    if (Direct) return <Direct />;
    return go ? <Lazy /> : <Loading what="Carregando tela" />;
  };
}
